/**
 * API client.
 *
 * A thin, typed wrapper over fetch that:
 *  - sends the session cookie with every request,
 *  - turns the server's structured error into a typed ApiError carrying the
 *    machine-readable code, so screens can react to a budget breach or a locked
 *    period specifically rather than matching on prose,
 *  - never swallows an error.
 *
 * Nothing here is a security boundary. Authorisation is decided entirely by the
 * server on every request; this client simply reports what the server said.
 */

export interface ApiErrorDetails {
  [key: string]: unknown;
}

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details: ApiErrorDetails;

  constructor(code: string, message: string, status: number, details: ApiErrorDetails = {}) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.details = details;
  }

  /** True when the caller may retry after supplying something extra. */
  get isRecoverable(): boolean {
    return ['BUDGET_EXCEEDED', 'DUPLICATE_DETECTED', 'MATCH_DISCREPANCY'].includes(this.code);
  }
}

const BASE = '/api';

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${BASE}${path}`, {
      credentials: 'include',
      ...init,
      headers: {
        Accept: 'application/json',
        ...(init.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
        ...init.headers,
      },
    });
  } catch {
    throw new ApiError(
      'NETWORK',
      'The server could not be reached. Check that it is running, then try again.',
      0,
    );
  }

  if (response.status === 204) return undefined as T;

  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) {
    if (!response.ok) {
      throw new ApiError('INTERNAL', `The server returned ${response.status}.`, response.status);
    }
    return (await response.text()) as unknown as T;
  }

  const body = (await response.json()) as T & { error?: { code: string; message: string; details?: ApiErrorDetails } };

  if (!response.ok) {
    const error = body.error;
    throw new ApiError(
      error?.code ?? 'INTERNAL',
      error?.message ?? `The server returned ${response.status}.`,
      response.status,
      error?.details ?? {},
    );
  }
  return body;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', body: body === undefined ? undefined : JSON.stringify(body) }),
  del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  upload: <T>(path: string, form: FormData) => request<T>(path, { method: 'POST', body: form }),

  /** Download a file the server generates (CSV export, BACS file, document). */
  download: async (path: string, filename?: string): Promise<void> => {
    const response = await fetch(`${BASE}${path}`, { credentials: 'include' });
    if (!response.ok) {
      let message = `The download failed (${response.status}).`;
      try {
        const body = (await response.json()) as { error?: { message?: string } };
        if (body.error?.message) message = body.error.message;
      } catch {
        // Body was not JSON; the generic message stands.
      }
      throw new ApiError('INTERNAL', message, response.status);
    }
    const blob = await response.blob();
    const disposition = response.headers.get('content-disposition') ?? '';
    const match = /filename="?([^"]+)"?/.exec(disposition);
    const name = filename ?? match?.[1] ?? 'download';

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = name;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  },

  /** Post rows to the server's CSV exporter and download the result. */
  exportCsv: async (payload: {
    reportName: string;
    parameters?: string;
    columns: Array<{ key: string; label: string; type?: 'text' | 'money' | 'number' | 'date' }>;
    rows: Array<Record<string, unknown>>;
    filename?: string;
  }): Promise<void> => {
    const response = await fetch(`${BASE}/reports/export/csv`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      throw new ApiError('INTERNAL', 'The export failed.', response.status);
    }
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download =
      payload.filename ?? `${payload.reportName.toLowerCase().replace(/[^a-z0-9]+/g, '-')}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  },
};
